import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError, Observable, of} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { URLS } from '../constants/URLS';
import { CategoryService } from './category.service';
import { getVideoThumbnail } from '../utils/media-url.utils';
import { AuthenticateserviceService } from './authenticateservice.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class MycontentserviceService {
  selectedTab: string = 'download';
  recentDownloadedofflineUri: string;
  popUpModalList = [];
  watchHistoryData = [];
  watchListData = [];
  downloadListData = [];
  downloadedCount = 0;
  watchHistoryCount = 0;
  watchListCount = 0;

  constructor(
    private http: HttpClient,
    private categService: CategoryService,
    private authService: AuthenticateserviceService,
    private universalApi: ApiService
  ) { }

  getContentOfUser(userId) {
    let body = new URLSearchParams();
    body.set("user_id", userId);
    
    return this.universalApi.postFormWithStatusCheck(URLS.getUserContent, body.toString()).pipe(
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  getUserWatchHistory() {
    return this.universalApi.get(URLS.getUserWatchHistory).pipe(
      map(responseData => {
        if (responseData.status == 0) {
          return responseData.data;
        } else {
          return undefined;
        }
      }),
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  isInWatchlist(vidLibId: string): Observable<boolean> {
    if (this.authService.isGuest()) {
      return of(false);
    }
    const userId = localStorage.getItem('userId');
    return this.getWatchListOfUser(userId).pipe(
      map(watchlist => watchlist.some(item => item.vidLibId == vidLibId)),
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  getWatchListOfUser(userId) {
    if (this.authService.isGuest()) {
      return of([]);
    }
    let body = new URLSearchParams();
    body.set("user_id", userId);
    body.set("watch_list", "watch_list");
    
    return this.universalApi.get(URLS.getUserWatchList).pipe(
      map(responseData => {
        if (responseData.status == 0) {
          return responseData.data;
        } else {
          return undefined;
        }
      }),
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  addInWatchList(userId, videoId) {
    let body = new URLSearchParams();
    body.set("user_id", userId);
    body.set("vid_lib_id", videoId);
    
    return this.universalApi.postForm(URLS.addInWatchList, body.toString()).pipe(
      map(responseData => {
        return responseData;
      }),
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  removeFromWatchList(userId, videoId) {
    let body = new URLSearchParams();
    body.set("user_id", userId);
    body.set("vid_lib_id", videoId);
    return this.universalApi.postForm(URLS.removeFromWatchList, body.toString()).pipe(
      map(responseData => {
        return responseData;
      }),
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  updateUserVideoTracking(req) {
    return this.universalApi.postWithStatusCheck(URLS.videoPlayBackUrl, req).pipe(
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  getWatchHistory(page: number = 1, limit: number = 20, sort: string = 'desc', channelId?: string) {
    let url = `${URLS.getWatchHistory}?page=${page}&limit=${limit}&sort=${sort}`;
    if (channelId) {
      url += `&channel_id=${channelId}`;
    }
    return this.universalApi.get(url).pipe(
      map(res => {
        const items = res?.data || [];
        return items.map(item => {
          const durationMs = item.progress > 0 ? (item.lastPositionMs * 100) / item.progress : 0;
          const remainingMs = Math.max(0, durationMs - item.lastPositionMs);
          return {
            ...item,
            watchedPercentage: item.progress,
            secondsWatched: item.lastPositionMs / 1000,
            remainingTime: this.formatRemainingTime(remainingMs)
          };
        });
      }),
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  private formatRemainingTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m left`;
    }
    if (minutes > 0) {
      return `${minutes} min left`;
    }
    return '';
  }

  insertWatchHistory(req: { vidLibId: number, lastPositionMs: number, durationMs: number, channelId: string, incrementWatchTimeMs: number }) {
    return this.universalApi.post(URLS.insertWatchHistory, req).pipe(
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  getContinueWatching(page: number = 1, limit: number = 20, sort: string = 'desc', channelId?: string) {
    if (this.authService.isGuest()) {
      return of([]);
    }
    let url = `${URLS.getContinueWatching}&page=${page}&limit=${limit}&sort=${sort}`;
    if (channelId) {
      url += `&channel_id=${channelId}`;
    }
    return this.universalApi.get(url).pipe(
      map(res => {
        const items = res?.data || [];
        return items.map(item => {
          const durationMs = item.progress > 0 ? (item.lastPositionMs * 100) / item.progress : 0;
          const remainingMs = Math.max(0, durationMs - item.lastPositionMs);
          return {
            ...item,
            watchedPercentage: item.progress,
            secondsWatched: item.lastPositionMs / 1000,
            remainingTime: this.formatRemainingTime(remainingMs)
          };
        });
      }),
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  deleteWatchHistoryEntry(vidLibId: number) {
    return this.universalApi.delete(`${URLS.deleteWatchHistoryEntry}${vidLibId}`).pipe(
      catchError(errorRes => {
        return throwError(errorRes);
      })
    );
  }

  getImageBlob(imageUrl) {
    const token: string = localStorage.getItem('token');
    fetch(`${imageUrl}`, {headers: {'Authorization': 'Bearer ' + token}}).then(async response => {
      const blobRepsonse = await response.blob();
      const urlCreator = window.URL || window.webkitURL;
        return urlCreator.createObjectURL(blobRepsonse);
    });
  }

  setPopUpList(comp) {
    if(this.popUpModalList.length) {
      this.popUpModalList = this.popUpModalList.map( popUp=> {
        if(popUp.id !== comp.id){
          popUp.popUpBool = false;
          return comp;
        }
          return popUp;
      });
    } else {
      this.popUpModalList.push(comp);
    }
  }

  getChannelIcon(channelId: string) {
    return this.universalApi.get(URLS.channelIconBaseUrl + channelId + URLS.channelIconThumbUrl).pipe(
      map(responseData => {
        console.log("response data of channel icon", responseData);
        return responseData;
      }),
      catchError(errorRes => {
        return throwError(errorRes);
      })
    ).toPromise();
  }

  transformVideoData(videoData: any, type: string): any {
    const properties = videoData?.properties;
    const vidLibId = videoData.vidLibId;
    const videoType = videoData.videoType;
    const thumbnailFileNameProp = properties.find(p => p.name === 'files');
    const thumbnailFileName = thumbnailFileNameProp ? thumbnailFileNameProp.value.split(',').pop().split('=')[0] : '';
    const thumbnailUrl = thumbnailFileName ? `${thumbnailFileName}` : '';

    const files = thumbnailFileNameProp ? thumbnailFileNameProp.value.split(',') : '';
    const titleImg = files ? files?.find(file => file.includes('title_img')) : '';
  
    const durationProp = properties.find(p => p.name === 'duration');
    const durationString = durationProp ? durationProp.value : '00:00:00';
    const durationParts = durationString.split(':');
    const durationInSeconds = (+durationParts[0] * 60 * 60) + (+durationParts[1] * 60) + (+durationParts[2]);
  
    const fileSizeProp = properties.find(p => p.name === 'file_sizes');
    const fileSizeString = fileSizeProp ? fileSizeProp.value.split(',') : [];
    const videoFileSizeProp = fileSizeString.find(f => f.includes('_video.mp4'));
    const videoFileSize = videoFileSizeProp ? videoFileSizeProp.split('=')[1] : '';
    const modifiedDuration = this.convertSecondsToHoursMinutes(durationProp.value);
    const year = this.extractYear(videoData.uploadedTime);
    const trailers = JSON.parse(properties.find(p => p.name === 'trailers')?.value || '[]');
    const latestTrailer = trailers.length ? trailers[trailers.length - 1] : null;
    const genres = properties.find(p => p.name === 'genre')?.value || '';

    const selectedItem = {
      title: properties.find(p => p.name === 'title')?.value || '',
      channelId: videoData.channelId,
      viewCategory: videoData.viewCategory || '',
      videoDuration: durationString,
      content_type: properties.find(p => p.name === 'content_type')?.value || '',
      pg_rating: properties.find(p => p.name === 'pg_rating')?.value || '',
      description: properties.find(p => p.name === 'description')?.value || '',
      starring: properties.find(p => p.name === 'starring')?.value || '',
      cast: JSON.parse(properties.find(p => p.name === 'cast')?.value || '[]'),
      crew: JSON.parse(properties.find(p => p.name === 'crew')?.value || '[]'),
      chapters: JSON.parse(properties.find(p => p.name === 'chapters')?.value || '[]'),
      external_source: properties.find(p => p.name === 'external_source')?.value || '[]',
      playback_path: properties.find(p => p.name === 'playback_path')?.value || '[]',
      trailers: trailers,
      latest_trailer: latestTrailer?.url,
      hlsManifest: trailers[0]?.hlsManifest,
      mpdManifest: trailers[0]?.mpdManifest,
      languages: properties.find(p => p.name === 'language')?.value || '',
      category: properties.find(p => p.name === 'category')?.value || '',
      channel_desc: properties.find(p => p.name === 'channel_desc')?.value || '',
      ppv_subscribed: properties.find(p => p.name === 'ppv_subscribed')?.value || '',
      ppv_unsubscribed: properties.find(p => p.name === 'ppv_unsubscribed')?.value || '',
      ppv_valid_for: properties.find(p => p.name === 'ppv_valid_for')?.value || '',
      playback_duration: properties.find(p => p.name === 'playback_duration')?.value || '',
      languagesAvailable: properties.find(p => p.name === 'languages_available')?.value || '',
      season_number: properties.find(p => p.name === 'season_number')?.value || '',
      episode_number: properties.find(p => p.name === 'episode_number')?.value || '',
      series_id: properties.find(p => p.name === 'series.id')?.value || '',
      season_id: properties.find(p => p.name === 'season.id')?.value || '',
      titleLanguages: properties.find(p => p.name === 'titleLanguages')?.value?.split(',') || [],
      bg: getVideoThumbnail(vidLibId),
      thumbnailUrl: thumbnailUrl,
      durationInSeconds: durationInSeconds,
      videoFileSize: videoFileSize,
      vidLibId: vidLibId,
      videoType: videoType,
      modifiedDuration: modifiedDuration,
      uploadedTime: this.categService.calculateModifiedDate(videoData.uploadedTime),
      secondsWatched: videoData.secondsWatched || 0,
      watchedPercentage: this.getWatchedPercentage(durationInSeconds.toString(), videoData.secondsWatched),
      videoCategory: type,
      preparedVideoType: this.prepareSelectedVideoType(properties),
      views: videoData.views || '',
      tags: videoData.tag || '',
      title_img: titleImg,
      genres: genres ? genres.split(','): [] ,
      year: year,
      imageError: false,
      titleImageError: false,
      mediaLayout: properties.find(p => p.name === 'media-layout')?.value || '',
      aspectRatio: properties.find(p => p.name === 'scale')?.value || '',
      currencyCode: properties.find(p => p.name === 'currencyCode')?.value || 'INR',
      currencySymbol: properties.find(p => p.name === 'currencySymbol')?.value || '',
      showBadges: videoData.showBadges,
      badge: videoData.badge,
      mediaId: videoData.mediaId
    };
  
    return selectedItem;
  }

  getWatchedPercentage(watchedSeconds: string, durationInSeconds: string): number {
    const totalDuration = parseInt(durationInSeconds, 10);
    const watchedSecond = parseInt(watchedSeconds, 10);
    if (isNaN(totalDuration) || isNaN(watchedSecond) || totalDuration <= 0 || watchedSecond < 0) {
      return 0;
    }
    const percentage = (watchedSecond / totalDuration) * 100;
    const clampedPercentage = Math.min(Math.max(percentage, 0), 100);
    return clampedPercentage;
  }
  
  prepareSelectedVideoType(dataOfVideo: Array<{ name: string; value: string }>): string {
    const getValueByName = (name: string): string | null => {
        const item = dataOfVideo.find(property => property.name === name);
        return item ? item.value : null;
    };
    const isFree = getValueByName("free_video") === "true"; 
    const ppvSubscribed = parseInt(getValueByName("ppv_subscribed") || "0", 10); 
    const ppvUnsubscribed = parseInt(getValueByName("ppv_unsubscribed") || "0", 10); 
    if (isFree) {
        return "FREE";
    } 
    if ((ppvSubscribed == 0 || ppvSubscribed > 0)  && ppvUnsubscribed > 0) {
        return 'PPV'; 
    } 
    if (ppvSubscribed == 0 && ppvUnsubscribed == 0) {
        return 'Sub'; 
    } 
    return "FREE"; 
  }

  transformcCPrimeData(data) {
    for(const category of data.category_data) { 
      category.categoryId = data.categoryId;
      category.year = this.extractYear(category.uploadedTime);
      category.videoCategory = 'ccPrime';
      category.modifiedDate = this.authService.calculateModifiedDate(category.uploadedTime);
      if(category?.properties) {
        for(const prop of category?.properties) {
          switch(prop.name) {
            case 'title': category.title = prop.value; break;
            case 'files': const filesList = prop.value.split(',');
              for(const file of filesList) {
                if(file.includes("thumb_v")) {
                  category.thumbnailv = file;
                  break;
                }
              }
            break;
            case 'description': category.description = prop.value; break;
            case 'duration': category.videoDuration = prop.value.startsWith('00:') ? prop.value.slice(3) : prop.value;
            category.duration = this.convertSecondsToHoursMinutes(category.videoDuration); break;
            case 'file_sizes': category.videoFileSize = this.findVideoFileSize(prop.value); break;
            case 'free_video': category.isFreeVideo = prop.value; break;
            case 'channel': category.channelId = prop.value; break;
            case 'category': category.genre = prop.value; break;
            case 'pg_rating': category.rating = prop.value; break;
            case 'starring': category.starring = prop.value; break;
            case 'ppv_subscribed': category.sub_value = prop.value; break; 
            case "ppv_unsubscribed": category.unSub_value = prop.value; break;
            case 'series.id': category.series_id = prop.value; break; 
            case 'season_number': category.season_number = prop.value; break;
            case 'episode_number': category.episode_number = prop.value; break;
            case 'channel_desc': category.channel_desc = prop.value; break;
            case 'scale': category.aspectRatio = prop.value; break;
            case 'currencySymbol': category.currencySymbol = prop.value; break;
            case 'currencyCode': category.currencyCode = prop.value; break;
            case 'media-layout': category.mediaLayout = prop.value; break;
            case 'trailers': category.trailers = JSON.parse(prop.value); break;
            case 'size': const valueInMb = Math.floor((prop.value/1024)/1024);  
            category.videoSizeInMb = valueInMb + 'Mb';
          }
        }
      }
      category.preparedVideoType = this.prepareVideoType(category);
    }
  }

  transformData(data:any) {
    data.year = this.extractYear(data.uploadedTime);
    if(data?.properties) {
      for(const prop of data.properties) {
        switch(prop.name) {
          case 'title': data.title = prop.value; break;
          case 'files':
            const filesList = prop.value.split(',');
            for (const file of filesList) {
              if (!data.thumbnail && file.includes("thumb_v")) {
                data.thumbnail = file;
              }
              if (!data.title_img && file.includes("title_img")) {
                data.title_img = file;
              } else {
                data.title_img = '';
              }
              if (data.thumbnail && data.title_img) break;
            }
          break;
          case 'description': data.description = prop.value; break;  
          case 'duration': data.videoDuration = prop.value;
          data.durationInSeconds = this.convertTimeToSeconds(data?.videoDuration);
          data.duration = this.convertSecondsToHoursMinutes(data?.videoDuration); break;
          case 'file_sizes': data.videoFileSize = this.findVideoFileSize(prop.value); break;
          case 'free_video': data.isFreeVideo = prop.value; break;
          case 'channel': data.channelId = prop.value; break;
          case 'channel_desc': data.channel_desc = prop.value; break;
          case 'series.id': data.series_id = prop.value; break; 
          case 'season_number': data.season_number = prop.value; break;
          case 'series.name': data.series_name = prop.value; break;
          case 'episode_number': data.episode_number = prop.value; break;
          case 'pg_rating': data.pg_rating = prop.value; break;
          case 'starring': data.starring = prop.value; break;
          case 'ppv_subscribed': data.sub_value = prop.value; break; 
          case "ppv_unsubscribed": data.unSub_value = prop.value; break;
          case "ppv_valid_for": data.ppv_valid_for = prop.value; break; 
          case 'content_sub_type': data.content_sub_type = prop.value; break;
          case 'genre': data.genres = (prop?.value).split(',') || ''; break;
          case 'category': data.category = prop.value; break;
          case 'language': data.language = prop.value; break;
          case 'currencySymbol': data.currencySymbol = prop.value; break;
          case 'currencyCode': data.currencyCode = prop.value; break;
          case 'media-layout': data.mediaLayout = prop.value; break;
          case 'trailers': data.trailers = JSON.parse(prop.value); break;
          case 'size': const valueInMb = Math.floor((prop.value/1024)/1024); data.videoSizeInMb = valueInMb + 'Mb';
        }
      }
    } 
    data.latestTrailer = data?.trailers?.length ? data?.trailers[data?.trailers?.length - 1] : null;
    data.preparedVideoType = this.prepareVideoType(data);
    data.watchedPercentage = this.getWatchedPercentage(data?.secondsWatched, data?.durationInSeconds);
    data.remainingTime = this.getRemainingTime(data?.secondsWatched, data?.durationInSeconds);
    data.bg = getVideoThumbnail(data);
    data.imageError = false;
    data.titleImageError = false;
  }
  
  getRemainingTime(secondsWatched: number, durationInSeconds: number): string {
    if (!secondsWatched || !durationInSeconds) {
      const timeStr = this.secondsToTimeString(durationInSeconds || 0);
      return this.convertSecondsToHoursMinutes(timeStr);
    }
    
    const remainingSeconds = durationInSeconds - secondsWatched;
    
    if (remainingSeconds <= 0) {
      return "Completed";
    }
    
    const timeStr = this.secondsToTimeString(remainingSeconds);
    return this.convertSecondsToHoursMinutes(timeStr) + " left";
  }

  private secondsToTimeString(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  prepareVideoType(category: { isFreeVideo: string; sub_value: string; unSub_value: string }): string | undefined {
    if (category.isFreeVideo === 'true') {
      return 'FREE';
    }
    if (category?.sub_value  && category?.unSub_value !== '0') {
      return 'PPV';
    }
    if (category.sub_value === '0' && category.unSub_value === '0') {
      return 'Sub';
    }
    return 'FREE'; 
  }

  extractMoreYear(dateString: string): number | null {
    if (!dateString) {
      return null; 
    }
    const parts = dateString.split(' ');
    if (parts.length < 2) {
      return null;
    }
    const datePart = parts[0];
    const dateParts = datePart.split('-');
    if (dateParts.length !== 3) {
      return null; 
    }
    const [day, month, yearString] = dateParts;
    const year = parseInt(yearString, 10);
    if (isNaN(year)) {
      return null;
    }
    return year;
  }

  findVideoFileSize(data: string) {
    const ele = data.split(',').find(item => item.includes('video.mp4'));
    if(ele) {
      return ele.split("=")[1];
    }
    return 0;
  }

  convertSecondsToHoursMinutes(inputTime: string): string {
    const [hours, minutes, seconds] = inputTime?.split(':').map(Number);
    let formattedTime = '';
    if (hours > 0) {
      formattedTime += hours + 'h ';
    }
    formattedTime += minutes + 'min';
    return formattedTime;
  }

  convertTimeToSeconds(timeStr) {
    const [hours, minutes, seconds] = timeStr?.split(':').map(Number);
    const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;
    return totalSeconds;
  }

  extractYear(dateString: string): number {
    const parts = dateString.split(' ');
    const datePart = parts[0]; 
    const [day, month, yearString] = datePart.split('-');
    const year = parseInt(yearString);
    return year;
  }
  
  private license: { data: Uint8Array; expiration: number } | null = null;
  
  setLicense(license: Uint8Array, expiration: number): void {
    this.license = { data: license, expiration: expiration };
  }
  
  getLicense(): Uint8Array | null {
    if (this.license && Date.now() < this.license.expiration) {
      return this.license.data;
    }
    return null;
  }
}